import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import "./../../../assets/css/general/ApplyPage/ApplyPage.css";

const AI_STEPS = [
  {
    id: "start",
    q: "What describes you best?",
    opts: [
      { label: "I want to learn & grow", next: "student_level" },
      { label: "I want to teach & mentor", next: "mentor_exp" },
      { label: "I'm exploring options", next: "explore" },
    ],
  },
  {
    id: "student_level",
    q: "Where are you in your journey?",
    opts: [
      { label: "Just starting out", result: "student" },
      { label: "Some experience, leveling up", result: "student" },
      { label: "Senior but want to keep growing", result: "student" },
    ],
  },
  {
    id: "mentor_exp",
    q: "How many years of professional experience?",
    opts: [
      { label: "Less than 2 years", result: "student" },
      { label: "2 to 5 years", result: "mentor" },
      { label: "5+ years", result: "mentor" },
    ],
  },
  {
    id: "explore",
    q: "Which excites you more?",
    opts: [
      { label: "Competing in Code Arena battles", result: "student" },
      { label: "Guiding someone's career", result: "mentor" },
    ],
  },
];

const RESULTS = {
  student: {
    icon: "★",
    headline: "You belong on the Student path",
    body: "Techify AI will craft your personalized roadmap. Your Code Arena debut awaits.",
    cta: "Start Learning",
    route: "/mentors",
  },
  mentor: {
    icon: "◆",
    headline: "You belong on the Mentor path",
    body: "Students are waiting for exactly your expertise. Apply and start making an impact today.",
    cta: "Become a Mentor",
    route: "/mentor/application",
  },
};

function AIModal({ onClose, navigate }) {
  const [stepId, setStepId] = useState("start");
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);

  const step = AI_STEPS.find((s) => s.id === stepId);

  function choose(opt) {
    if (opt.result) {
      setResult(opt.result);
      return;
    }
    setHistory((h) => [...h, stepId]);
    setStepId(opt.next);
  }

  function back() {
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setStepId(prev);
    setResult(null);
  }

  return (
    <div className="apm-backdrop" onClick={onClose}>
      <div className="apm-box" onClick={(e) => e.stopPropagation()}>
        <div className="apm-top">
          <span className="apm-chip">⬡ Techify AI</span>
          <button className="apm-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {result ? (
          <div className="apm-result">
            <div className="apm-result-icon">{RESULTS[result].icon}</div>
            <h3 className="apm-result-title">{RESULTS[result].headline}</h3>
            <p className="apm-result-body">{RESULTS[result].body}</p>
            <button
              className={`apm-result-cta apm-result-cta--${result}`}
              onClick={() => navigate(RESULTS[result].route)}
            >
              {RESULTS[result].cta} →
            </button>
            <button
              className="apm-link"
              onClick={() => {
                setResult(null);
                setStepId("start");
                setHistory([]);
              }}
            >
              ↩ Start over
            </button>
          </div>
        ) : (
          <>
            <p className="apm-question">{step?.q}</p>
            <div className="apm-opts">
              {step?.opts.map((o, i) => (
                <button key={i} className="apm-opt" onClick={() => choose(o)}>
                  {o.label}
                </button>
              ))}
            </div>
            <div className="apm-foot">
              {history.length > 0 && (
                <button className="apm-link" onClick={back}>
                  ← Back
                </button>
              )}
              <div className="apm-dots">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={`apm-dot${i === history.length ? " apm-dot--on" : ""}`}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ApplyPage() {
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    const esc = (e) => {
      if (e.key === "Escape") setAiOpen(false);
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  return (
    <main className="ap">
      {/* Top */}
      <section className="ap-top">
        <p className="ap-eyebrow">Techify · Choose Your Path</p>
        <h1 className="ap-heading">
          Join the Ecosystem
          <br />
          of Excellence
        </h1>
        <p className="ap-tagline">
          Learn, grow, and earn — whether you're a student or a mentor.
        </p>
      </section>

      {/* Split panels */}
      <div className="ap-split">
        {/* LEFT — Student */}
        <div className="ap-panel ap-panel--student">
          <div className="ap-panel-inner">
            <span className="ap-label">For Students</span>

            <h2 className="ap-panel-title">
              Start Your
              <br />
              Learning Journey
            </h2>

            <p className="ap-panel-desc">
              Learn with AI Tutor, compete in Code Arena, and follow a
              personalized roadmap built around your goals.
            </p>

            <ul className="ap-feats">
              <li>
                <span className="ap-fi">◈</span>
                <div>
                  <b>AI Tutor Guidance</b>
                  <span>Your 24/7 AI learning companion</span>
                </div>
              </li>
              <li>
                <span className="ap-fi">⬡</span>
                <div>
                  <b>Code Arena Battles</b>
                  <span>Compete, rank up, and win</span>
                </div>
              </li>
              <li>
                <span className="ap-fi">◉</span>
                <div>
                  <b>Skill Progression</b>
                  <span>XP, levels &amp; adaptive roadmap</span>
                </div>
              </li>
            </ul>

            <div className="ap-cta-area">
              <button
                className="ap-btn ap-btn--student"
                onClick={() => navigate("/mentors")}
              >
                Start Learning
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
              <span className="ap-micro">
                Free to join · No credit card needed
              </span>
            </div>
          </div>

          <div className="ap-bg-deco" aria-hidden="true">
            ⬡
          </div>
        </div>

        {/* RIGHT — Mentor */}
        <div className="ap-panel ap-panel--mentor">
          <div className="ap-panel-inner">
            <span className="ap-label ap-label--inv">For Mentors</span>

            <h2 className="ap-panel-title ap-panel-title--inv">
              Share Your
              <br />
              Expertise
            </h2>

            <p className="ap-panel-desc ap-panel-desc--inv">
              Guide students, build your influence, and grow with a powerful
              mentorship system backed by Techify AI.
            </p>

            <ul className="ap-feats ap-feats--inv">
              <li>
                <span className="ap-fi ap-fi--inv">◈</span>
                <div>
                  <b>Mentor Dashboard</b>
                  <span>Real-time student progress</span>
                </div>
              </li>
              <li>
                <span className="ap-fi ap-fi--inv">⬡</span>
                <div>
                  <b>Student Interaction</b>
                  <span>Sessions, feedback &amp; 1-on-1s</span>
                </div>
              </li>
              <li>
                <span className="ap-fi ap-fi--inv">◉</span>
                <div>
                  <b>Earn &amp; Impact</b>
                  <span>Monetize knowledge &amp; track influence</span>
                </div>
              </li>
            </ul>

            <div className="ap-stats">
              {[
                ["4.9★", "Avg rating"],
                ["$120/hr", "Avg earnings"],
                ["94%", "Match rate"],
              ].map(([v, l]) => (
                <div key={l} className="ap-stat">
                  <b>{v}</b>
                  <span>{l}</span>
                </div>
              ))}
            </div>

            <div className="ap-cta-area">
              <button
                className="ap-btn ap-btn--mentor"
                onClick={() => navigate("/mentor/application")}
              >
                Become a Mentor
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
              <span className="ap-micro ap-micro--inv">
                Apply in 5 min · Verified badge
              </span>
            </div>
          </div>

          <div className="ap-bg-deco" aria-hidden="true">
            ⬡
          </div>
        </div>
      </div>

      {/* AI strip */}
      <section className="ap-guide">
        <div className="ap-guide-inner">
          <p className="ap-guide-label">
            <span className="ap-guide-dot" />
            Not sure where to start?
          </p>
          <h3 className="ap-guide-heading">Let Techify AI Guide You</h3>
          <p className="ap-guide-sub">
            Answer 3 quick questions — our AI matches you to the right path
            instantly.
          </p>
          <button className="ap-guide-btn" onClick={() => setAiOpen(true)}>
            <span className="ap-guide-hex">⬡</span>
            Let Techify AI Guide Me
          </button>
        </div>
      </section>

      {aiOpen && (
        <AIModal onClose={() => setAiOpen(false)} navigate={navigate} />
      )}
    </main>
  );
}
